import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, FileText, Minus, Plus } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

interface Props {
  artifactId: string;
  initialPage: number;
  query: string;
  label: string;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function PdfContextViewer({ artifactId, initialPage, query, label }: Props) {
  const [page, setPage] = useState(initialPage || 1);
  const [pages, setPages] = useState(0);
  const [scale, setScale] = useState(1.08);
  const viewportRef = useRef<HTMLDivElement>(null);
  const url = `/api/artifacts/${artifactId}/content`;
  const normalizedQuery = useMemo(() => query.trim().replace(/\s+/g, " "), [query]);

  useEffect(() => setPage(initialPage || 1), [initialPage, artifactId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const mark = viewportRef.current?.querySelector("mark.pdf-hit");
      mark?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }, 550);
    return () => window.clearTimeout(timer);
  }, [page, scale, normalizedQuery]);

  const renderText = ({ str }: { str: string }) => {
    if (!normalizedQuery || normalizedQuery.length < 3) return str;
    const direct = new RegExp(escapeRegExp(normalizedQuery), "ig");
    if (direct.test(str)) {
      return str.replace(direct, (match) => `<mark class="pdf-hit">${match}</mark>`);
    }
    const words = normalizedQuery.split(" ").filter((word) => word.length >= 4).slice(0, 4);
    if (!words.length) return str;
    const partial = new RegExp(words.map(escapeRegExp).join("|"), "ig");
    return str.replace(partial, (match) => `<mark class="pdf-hit pdf-hit-partial">${match}</mark>`);
  };

  return (
    <section className="pdf-context" aria-label={`PDF context: ${label}`}>
      <header className="pdf-toolbar">
        <div className="pdf-title"><FileText size={16} /><span>{label}</span></div>
        <div className="pdf-controls">
          <button aria-label="Previous PDF page" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page <= 1}><ChevronLeft size={16} /></button>
          <span>{page} / {pages || "…"}</span>
          <button aria-label="Next PDF page" onClick={() => setPage((value) => Math.min(pages || value + 1, value + 1))} disabled={Boolean(pages) && page >= pages}><ChevronRight size={16} /></button>
          <button aria-label="Zoom out" onClick={() => setScale((value) => Math.max(.7, value - .12))}><Minus size={15} /></button>
          <button aria-label="Zoom in" onClick={() => setScale((value) => Math.min(2, value + .12))}><Plus size={15} /></button>
          <a href={`${url}#page=${page}`} target="_blank" rel="noreferrer" aria-label="Open PDF in new tab"><ExternalLink size={15} /></a>
        </div>
      </header>
      <div className="pdf-match-strip"><span>Matched context</span><strong>{query}</strong></div>
      <div className="pdf-viewport" ref={viewportRef}>
        <Document
          file={url}
          onLoadSuccess={({ numPages }) => setPages(numPages)}
          loading={<div className="pdf-loading">Rendering the matched page…</div>}
          error={<div className="pdf-loading error">This PDF could not be rendered. Open it in a new tab.</div>}
        >
          <Page pageNumber={page} scale={scale} customTextRenderer={renderText} renderAnnotationLayer renderTextLayer />
        </Document>
      </div>
    </section>
  );
}

